<script setup lang="ts">
import { computed, watch } from 'vue';
import { useEditorStore } from '@/stores/editor';
import { useAssetsStore } from '@/stores/assets';

const editorStore = useEditorStore();
const assetsStore = useAssetsStore();

// Get PAK list dynamically from loaded atlas, categorized by type
const allPaks = computed(() => {
    return assetsStore.getPakNames();
});

// Categorize PAKs into tiles vs objects
const tilePaks = computed(() => {
    return allPaks.value.filter(pak => {
        const lower = pak.toLowerCase();
        return !lower.startsWith('objects') && !lower.startsWith('trees');
    });
});

// Stock maps also place sprites from the tile PAKs on the object layer (walls,
// structures), so the objects tab lists every PAK, object PAKs first
const objectPaks = computed(() => {
    const isObjectPak = (pak: string): boolean => {
        const lower = pak.toLowerCase();
        return lower.startsWith('objects') || lower.startsWith('trees');
    };
    return [...allPaks.value.filter(isObjectPak), ...tilePaks.value];
});

const currentPaks = computed(() => {
    return editorStore.paletteTab === 'tiles' ? tilePaks.value : objectPaks.value;
});

// Get sprites for selected PAK using the index (O(1) lookup)
const spritesInPak = computed(() => {
    if (!editorStore.selectedPak || !assetsStore.atlas) return [];
    return assetsStore.getSpritesForPak(editorStore.selectedPak);
});

// Select a PAK
function selectPak(pak: string): void {
    editorStore.selectedPak = pak;
    editorStore.selectedPaletteSpriteId = null;
}

// Select a sprite
function selectSprite(spriteId: number): void {
    editorStore.selectedPaletteSpriteId = spriteId;
}

// Reset selection when changing tabs
watch(() => editorStore.paletteTab, () => {
    editorStore.selectedPak = null;
    editorStore.selectedPaletteSpriteId = null;
});
</script>

<template>
    <div class="tile-palette">
        <!-- Tabs -->
        <div class="palette-tabs">
            <button
                class="tab-btn"
                :class="{ active: editorStore.paletteTab === 'tiles' }"
                @click="editorStore.paletteTab = 'tiles'"
            >
                Tiles
            </button>
            <button
                class="tab-btn"
                :class="{ active: editorStore.paletteTab === 'objects' }"
                @click="editorStore.paletteTab = 'objects'"
            >
                Objects
            </button>
        </div>

        <!-- Two-column layout -->
        <div class="palette-columns">
            <!-- Column 1: PAK List -->
            <div class="pak-list">
                <div class="column-header">PAK Files</div>
                <div class="list-scroll">
                    <button
                        v-for="pak in currentPaks"
                        :key="pak"
                        class="list-item"
                        :class="{ selected: editorStore.selectedPak === pak }"
                        @click="selectPak(pak)"
                    >
                        {{ pak }}
                    </button>
                </div>
            </div>

            <!-- Column 2: Sprite List (shown when PAK selected) -->
            <div class="sprite-list" v-if="editorStore.selectedPak">
                <div class="column-header">Sprites</div>
                <div class="list-scroll">
                    <button
                        v-for="spriteId in spritesInPak"
                        :key="spriteId"
                        class="list-item"
                        :class="{ selected: editorStore.selectedPaletteSpriteId === spriteId }"
                        @click="selectSprite(spriteId)"
                    >
                        Sprite {{ spriteId }}
                    </button>
                    <div v-if="spritesInPak.length === 0" class="empty-message">
                        No sprites found
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.tile-palette {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 280px;
}

.palette-tabs {
    display: flex;
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.tab-btn {
    flex: 1;
    padding: 10px;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.tab-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
}

.tab-btn.active {
    background: var(--bg-tertiary);
    color: var(--accent-color);
    border-bottom: 2px solid var(--accent-color);
}

.palette-columns {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.pak-list,
.sprite-list {
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--border-color);
    background: var(--bg-panel);
}

.pak-list {
    width: 140px;
    flex-shrink: 0;
}

.sprite-list {
    flex: 1;
}

.column-header {
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.list-scroll {
    flex: 1;
    overflow-y: auto;
}

.list-item {
    display: block;
    width: 100%;
    padding: 8px 12px;
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-primary);
    font-size: 12px;
    text-align: left;
    cursor: pointer;
    transition: background 0.1s ease;
}

.list-item:hover {
    background: var(--bg-tertiary);
}

.list-item.selected {
    background: var(--accent-color);
    color: white;
}

.empty-message {
    padding: 16px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
}
</style>
